import React from 'react';
import { ExclamationTriangleIcon, XCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const ErrorDisplay = ({ error, onClose, className = '' }) => {
  if (!error) return null;

  // Parse error object
  const getErrorInfo = () => {
    if (typeof error === 'string') {
      return {
        type: 'error',
        title: 'Error',
        message: error,
        details: null
      };
    }

    if (error.response) {
      const { status, data } = error.response;
      
      return {
        type: getErrorType(status),
        title: getErrorTitle(status, data),
        message: data?.message || error.message || 'An error occurred',
        details: data?.details,
        status: status,
        code: data?.error_code || data?.code,
        solution: data?.details?.solution,
        configurationRequired: data?.details?.configurationRequired,
        environment: data?.details?.environment,
        tokenStatus: data?.details?.tokenStatus
      };
    }

    return {
      type: 'error',
      title: 'Network Error',
      message: error.message || 'Unable to connect to server',
      details: null
    };
  };

  const getErrorType = (status) => {
    if (status >= 500) return 'server';
    if (status === 401) return 'auth';
    if (status === 403) return 'forbidden';
    if (status === 404) return 'notfound';
    return 'error';
  };

  const getErrorTitle = (status, data) => {
    if (data?.error) return data.error;
    
    switch (status) {
      case 400: return 'Bad Request';
      case 401: return 'Authentication Required';
      case 403: return 'Access Denied';
      case 404: return 'Not Found';
      case 500: return 'Server Error';
      default: return 'Error';
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'auth':
      case 'forbidden':
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      case 'server':
        return <XCircleIcon className="h-5 w-5" />;
      default:
        return <InformationCircleIcon className="h-5 w-5" />;
    }
  };

  const getColorClasses = (type) => {
    switch (type) {
      case 'auth':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'forbidden':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'server':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const errorInfo = getErrorInfo();

  return (
    <div className={`rounded-md border p-4 ${getColorClasses(errorInfo.type)} ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          {getIcon(errorInfo.type)}
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium">
            {errorInfo.title}
            {errorInfo.status && (
              <span className="ml-2 text-xs opacity-75">
                (Status: {errorInfo.status})
              </span>
            )}
          </h3>
          
          <div className="mt-2 text-sm">
            <p>{errorInfo.message}</p>
            
            {/* FBR Configuration Error Details */}
            {errorInfo.configurationRequired && (
              <div className="mt-3 p-3 bg-white bg-opacity-50 rounded border">
                <h4 className="font-medium text-sm mb-2">Configuration Required:</h4>
                <ul className="text-xs space-y-1">
                  {errorInfo.environment && (
                    <li><strong>Environment:</strong> {errorInfo.environment}</li>
                  )}
                  {errorInfo.tokenStatus && (
                    <li><strong>Token Status:</strong> {errorInfo.tokenStatus}</li>
                  )}
                  {errorInfo.solution && (
                    <li><strong>Solution:</strong> {errorInfo.solution}</li>
                  )}
                </ul>
              </div>
            )}

            {/* Code/Error Code */}
            {errorInfo.code && (
              <div className="mt-2">
                <span className="text-xs font-mono bg-white bg-opacity-50 px-2 py-1 rounded">
                  Code: {errorInfo.code}
                </span>
              </div>
            )}

            {/* Additional Details */}
            {errorInfo.details && typeof errorInfo.details === 'object' && !errorInfo.configurationRequired && (
              <div className="mt-3">
                <details className="text-xs">
                  <summary className="cursor-pointer font-medium">Show Details</summary>
                  <pre className="mt-2 p-2 bg-white bg-opacity-50 rounded overflow-auto">
                    {JSON.stringify(errorInfo.details, null, 2)}
                  </pre>
                </details>
              </div>
            )}

            {/* Validation Errors */}
            {Array.isArray(errorInfo.details) && (
              <div className="mt-3">
                <h4 className="font-medium text-sm mb-2">Validation Errors:</h4>
                <ul className="text-xs space-y-1">
                  {errorInfo.details.map((detail, index) => (
                    <li key={index}>
                      <strong>{detail.field}:</strong> {detail.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        
        {onClose && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex rounded-md p-1.5 hover:bg-white hover:bg-opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
              >
                <span className="sr-only">Dismiss</span>
                <XCircleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorDisplay;